// Единая функция копирования в буфер обмена для всего приложения.
//
// Требования: должна одинаково надёжно работать на десктопе (Windows/macOS/
// Linux, любой браузер), на Android (Chrome/Samsung Internet/системный
// WebView, с любой раскладкой и любой виртуальной клавиатурой — Gboard,
// SwiftKey и т.д., включая устройства без сервисов Google, например Huawei
// на EMUI со своей сборкой WebView) и на iOS/iPadOS (Safari и WebView
// внутри Telegram).
//
// ВАЖНО про порядок способов копирования: сначала пробуем СИНХРОННЫЙ способ
// через document.execCommand('copy'), а не современный асинхронный
// navigator.clipboard.writeText(). Причина: у Clipboard API внутри WebView
// Telegram на части Android-устройств permissions policy вокруг буфера
// обмена может тихо блокировать вызов — промис не резолвится и не
// отклоняется вообще, а просто зависает навсегда, из-за чего ни .then, ни
// .catch не срабатывают. execCommand('copy'), наоборот, выполняется прямо
// внутри обработчика клика синхронно, пока браузер однозначно считает это
// действием пользователя.
//
// text      — что копируем
// onSuccess — колбэк при успехе (например, показать тост "Скопировано")
// onError   — колбэк при неудаче
export function copyToClipboard(text, onSuccess, onError) {
  const value = text === null || text === undefined ? '' : String(text);

  // iOS Safari (и WebView на её движке) — единственная платформа, где
  // обычный textArea.select() иногда не выделяет значение по-настоящему.
  // Специальный обходной приём (contentEditable + Range/Selection) нужен
  // ТОЛЬКО там: если применить его на всех платформах подряд, он перебивает
  // и ЛОМАЕТ уже корректно работающее выделение textarea на десктопе и
  // Android — именно так предыдущая версия перестала копировать вообще
  // везде. Поэтому включаем его строго по условию, не трогая остальные
  // платформы вообще.
  const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Синхронная попытка через скрытое поле + execCommand('copy').
  // Возвращает true/false — успела ли команда реально сработать.
  const legacyCopySync = () => {
    // Запоминаем, что было в фокусе (например, поле ввода с открытой
    // клавиатурой Gboard/SwiftKey), чтобы вернуть фокус туда после
    // копирования — иначе клавиатура резко закрывается и пользователь
    // теряет место ввода.
    const previouslyFocused = document.activeElement;

    const textArea = document.createElement('textarea');
    textArea.value = value;
    // readonly — можно фокусировать и выделять текст, но не открывает
    // виртуальную клавиатуру на Android. disabled использовать нельзя:
    // disabled-поля вообще не получают фокус, из-за чего .select() и
    // document.execCommand('copy') молча ничего не делают.
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    // Уводим поле физически за пределы экрана (а не через opacity/visibility
    // — на части нестандартных Android WebView встречались случаи, когда
    // невидимый через opacity элемент не давал корректно выделить в нём текст).
    textArea.style.top = '0';
    textArea.style.left = '-9999px';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.fontSize = '16px'; // не даём iOS зумить страницу при фокусе
    document.body.appendChild(textArea);

    let copied = false;
    try {
      // Базовый способ, который работает на подавляющем большинстве
      // платформ (десктоп, Android) — именно на нём стоим по умолчанию.
      textArea.focus({ preventScroll: true });
      textArea.select();
      textArea.setSelectionRange(0, value.length);

      // Только для iOS — дополнительный, отдельный от базового способ
      // выделения через contentEditable+Range/Selection.
      if (isIOS) {
        textArea.contentEditable = 'true';
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        textArea.setSelectionRange(0, value.length);
      }

      copied = document.execCommand('copy');
    } catch (e) {
      copied = false;
    }

    if (isIOS) {
      try {
        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();
      } catch (e) {}
    }

    document.body.removeChild(textArea);

    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try { previouslyFocused.focus({ preventScroll: true }); } catch (e) {}
    }

    return copied;
  };

  if (legacyCopySync()) {
    if (onSuccess) onSuccess();
    return;
  }

  // execCommand не сработал (например, в некоторых десктопных браузерах он
  // отключён политикой безопасности страницы) — пробуем современный
  // асинхронный Clipboard API как запасной вариант, со сторожевым
  // таймаутом на случай, если промис вообще не ответит.
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function' && window.isSecureContext) {
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      if (onError) onError();
    }, 800);

    navigator.clipboard.writeText(value)
      .then(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        if (onSuccess) onSuccess();
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        if (onError) onError();
      });
  } else if (onError) {
    onError();
  }
}
